import { useEffect, useState } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import { Alert, LookupField, RegistrationLookupWrapper } from 'frame/components'
import { createDefaultModel } from '../../module'
import { hasRoles } from 'acl'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import { useForm, Controller, FormProvider } from 'react-hook-form'

import DetailsInfoWrapper from 'frame/components/details-info-wrapper'
import CardContent from 'frame/components/card-content'
import MuiTextfield from 'frame/components/mui-textfield'
import MuiFormSelect from 'frame/components/mui-form-select'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { zodResolver } from '@hookform/resolvers/zod'
import { useProfile } from 'features/account'
import { schema } from './schema'

const Form = ({
  relatedContract,
  disabled,
  error,
  fetchContractStatus,
  fleetIds,
  onClose,
  onFetchContract,
  onSubmit,
  onLookup,
  errorMessage,
  userRoles
}) => {
  const theme = useTheme()
  const profile = useProfile()
  const requestorName = profile?.nickname || profile?.name || [profile?.given_name, profile?.family_name].filter(Boolean).join(' ') || profile?.email || ''
  const requestorEmail = profile?.email || ''
  const [contract, setContract] = useState({})
  const [vehicle, setVehicle] = useState()
  const [values, setValues] = useState({
    ...createDefaultModel(),
    requestRucKm: 1000,
    requestorDriverName: requestorName,
    requestorDriverEmail: requestorEmail,
  })
  const disableButtons = hasRoles(['fleetcoordinator'], userRoles)
  const lookupContractData = relatedContract?.[vehicle?.agreementNo] || null
  const locked = disabled || ! contract?.agreementNo ? true : false

  const methods = useForm({
    resolver: zodResolver(schema),
    reValidateMode: 'all',
    defaultValues: {
      odometer: values?.odometer ?? '',
      requestRucKm: values?.requestRucKm ?? 1000,
      labelDeliveryAddress: values?.labelDeliveryAddress ?? '',
      comments: values?.comments ?? '',
    }
  })

  const hasContract = !!contract?.agreementNo

  const onRegoChange = (option) => {
    setContract({})
    setVehicle(option)
    const selectedRego = option?.value || option?.text
    if (selectedRego) {
      onFetchContract(selectedRego, fleetIds)
    }
  }

  const onInputChange = ({ target: { id, name, value } }) => {
    setValues({ ...values, [id || name]: value })
    methods.setValue(name, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const onSelectChange = (key, value) => {
    console.log(key, value)
    setValues({ ...values, [key]: value })
    methods.setValue(key, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const onSubmitForm = () => {
    onSubmit({
      ...values,
      requestRucKm: values?.requestRucKm ?? methods.getValues('requestRucKm') ?? 1000,
      requestorDriverName: requestorName || values?.requestorDriverName || '',
      requestorDriverEmail: requestorEmail || values?.requestorDriverEmail || '',
    })
  }
  useEffect(() => {
    if (lookupContractData?.agreementNo) {
      setContract(lookupContractData)
      setValues({
        ...values,
        costCentre: lookupContractData.costCentre || '',
        agreementNumber: lookupContractData.agreementNo || '',
        thirdPartyNumber: lookupContractData.thirdPartyNumber || '',
        rego: lookupContractData.registration || '',
        requestDate: moment().toISOString(),
        maturityDate: lookupContractData.maturityDate ? moment(lookupContractData.maturityDate).format('YYYY-MM-DD') : null,
        requestRucKm: values?.requestRucKm ?? 1000,
        requestorDriverName: requestorName || values?.requestorDriverName || '',
        requestorDriverEmail: requestorEmail || values?.requestorDriverEmail || '',
      })
      methods.setValue('costCentre', lookupContractData.costCentre)
    }
  }, [lookupContractData, requestorName, requestorEmail]) // eslint-disable-line

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormProvider>
        <form
          id="ruc-order-form"
          onSubmit={methods.handleSubmit(onSubmitForm)}>
          <CardContent>
            {error && (
              <Stack gap={1}>
                <Alert>
                  {errorMessage || 'Something went wrong while trying to submit RUC order.' }
                </Alert>
              </Stack>
            )}

            <Stack gap={4}>
              <Stack gap={2}>
                <Typography variant="h4" fontWeight={600}>
                  Vehicle
                </Typography>
                <Typography variant="bodyLarge">
                  Please select the vehicle you wish to order road user charges (RUC) licenses for.
                </Typography>
              </Stack>
              <Stack flex={1} gap={1}>
                <RegistrationLookupWrapper
                  lookupContractData={contract}
                  fetchContractStatus={fetchContractStatus}
                  lookupContractErrorMessage={errorMessage}
                  checkMaturityDate={false}>
                  <LookupField
                    id="registration"
                    disabled={disabled || disableButtons}
                    fullWidth
                    label="Vehicle Registration"
                    lookupPath="vehicleregistration"
                    value={vehicle}
                    variant="outlined"
                    validators={['required']}
                    errorMessages={['This field is required']}
                    onChange={onRegoChange}
                    onLookup={onLookup}
                    resourceModel="requestRucSummary"/>
                </RegistrationLookupWrapper>
              </Stack>
              {hasContract && (
                <>
                  <Stack gap={1}>
                    <DetailsInfoWrapper
                      dataId="relief-vehicle-booking"
                      items={[{
                        label: 'Driver',
                        value: contract.driver || '-',
                      },
                      {
                        label: 'Location',
                        value: contract.location || '-',
                      },
                      {
                        label: 'Cost Centre',
                        value: contract.costCentre || '-',
                      },
                      {
                        label: 'Maturity Date',
                        value: contract.maturityDate || '-',
                        format: 'Do MMMM YYYY',
                      },
                      {
                        label: 'Vehicle Notes',
                        value: contract.vehicleNotes || '-',
                      },
                      ]}/>
                  </Stack>
                </>
              )}
              <Stack gap={2}>
                <Typography variant="h4" fontWeight={600}>
                  Order details
                </Typography>
                <Typography variant="bodyLarge">
                  Please complete all required fields.
                </Typography>
                <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
                  <Stack width="50%">
                    <Controller
                      name="odometer"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MuiTextfield
                          {...field}
                          id="odometer"
                          name="odometer"
                          inputProps={{ maxLength: 50 }}
                          disabled={locked}
                          error={Boolean(methods.formState.errors?.odometer?.message)}
                          label="Current Odometer*"
                          value={values.odometer || ''}
                          onChange={onInputChange}/>
                      )}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.odometer?.message}
                    </Typography>
                  </Stack>
                  <Stack width="50%">
                    <Controller
                      name="requestRucKm"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MuiFormSelect
                          {...field}
                          id="requestRucKm"
                          labelId="requestRucKm-label"
                          disabled={locked}
                          label="Number of Units*"
                          includeEmptyOption
                          emptyOptionLabel="Select units"
                          options={[1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000, 11000, 12000, 13000, 14000, 15000, 16000, 17000, 18000, 19000, 20000]}
                          value={field.value ?? values.requestRucKm ?? 1000}
                          onChange={(event) => onSelectChange('requestRucKm', event.target.value)}/>
                      )}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.requestRucKm?.message}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
                  <Stack width="100%">
                    <Controller
                      name="labelDeliveryAddress"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MuiTextfield
                          {...field}
                          id="labelDeliveryAddress"
                          name="labelDeliveryAddress"
                          inputProps={{ maxLength: 50 }}
                          disabled={locked}
                          error={Boolean(methods.formState.errors?.labelDeliveryAddress?.message)}
                          label="Delivery Address for the label*"
                          value={values.labelDeliveryAddress || ''}
                          onChange={onInputChange}/>
                      )}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.labelDeliveryAddress?.message}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
                  <Stack width="100%">
                    <Controller
                      name="comments"
                      control={methods.control}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <MuiTextfield
                          {...field}
                          multiline
                          rows={4}
                          id="comments"
                          label="Comments"
                          inputProps={{ maxLength: 2000 }}
                          value={values.comments || ''}
                          onChange={onInputChange}
                          disabled={locked}/>
                      )}/>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            <Divider sx={{ mt: 3, mb: 2, borderColor: theme.palette.accent.sky }} />
            <Stack
              flexDirection={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              rowGap={2}>
              <Box width={{ xs: '100%', md: '205px' }}>
                <Button
                  fullWidth
                  color="error"
                  variant="outlined"
                  onClick={onClose}>
                  Close
                </Button>
              </Box>
              <Stack flexDirection="row" gap={1}>
                <Box width={{ xs: '100%', md: '215px' }}>
                  <Button
                    type="submit"
                    fullWidth
                    color="primary"
                    disabled={locked}
                    variant="contained">
                    Request RUCs
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </form>
      </FormProvider>
    </LocalizationProvider>
  )
}

Form.propTypes = {
  contracts: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  fetchContractStatus: PropTypes.string,
  fleetIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onFetchContract: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onLookup: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
}

export default Form
